import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Copy, Eye, EyeOff, Check, AlertTriangle, RefreshCw } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import AdminLayout from '../../components/AdminLayout';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";

export default function SettingsPage() {
  const { admin, fetchWithAuth } = useAuth();
  const [showApiKey, setShowApiKey] = useState(false);
  const [copied, setCopied] = useState(false);
  const [networkMode, setNetworkMode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [changing, setChanging] = useState(false);
  const [showMainnetWarning, setShowMainnetWarning] = useState(false);

  useEffect(() => {
    fetchNetworkMode();
  }, []);

  const fetchNetworkMode = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetchWithAuth(`${backendUrl}/api/admin/network-mode`);
      
      if (response.ok) {
        const data = await response.json();
        setNetworkMode(data);
      } else {
        console.error('Error fetching network mode');
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNetworkToggle = (checked) => {
    if (checked) {
      // Switching to MAINNET - show warning
      setShowMainnetWarning(true);
    } else {
      // Switching to TESTNET - no warning needed
      changeNetworkMode('testnet');
    }
  };

  const changeNetworkMode = async (network) => {
    setChanging(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetchWithAuth(`${backendUrl}/api/admin/network-mode`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ network })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        
        if (data.requires_restart) {
          toast.warning('El backend debe reiniciarse para aplicar cambios', {
            duration: 5000
          });
        }
        
        // Refresh network mode
        await fetchNetworkMode();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al cambiar modo de red');
      }
    } catch (error) {
      toast.error('Error al conectar con el servidor');
    } finally {
      setChanging(false);
      setShowMainnetWarning(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copiado al portapapeles');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="settings-page">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-[hsl(var(--brand-ink))]" style={{ fontFamily: 'var(--font-heading)' }}>
            Configuración
          </h1>
          <p className="text-[hsl(var(--muted-foreground))] mt-1">
            Gestiona la configuración del sistema
          </p>
        </div>

        {/* Network Mode Section */}
        <Card className="p-6 border-2" data-testid="network-mode-card">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="font-semibold text-[hsl(var(--brand-ink))] flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-orange-500" />
                Modo de Red
              </h3>
              <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
                Cambiar entre modo de pruebas y producción
              </p>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin text-[hsl(var(--brand-teal))]" />
            </div>
          ) : networkMode && (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-[hsl(var(--brand-sand))] rounded-lg">
                <div className="space-y-1">
                  <div className="flex items-center gap-3">
                    <Label htmlFor="network-toggle" className="text-base font-medium">
                      Modo Producción (MAINNET)
                    </Label>
                    <Switch
                      id="network-toggle"
                      checked={networkMode.is_mainnet}
                      onCheckedChange={handleNetworkToggle}
                      disabled={changing}
                      data-testid="network-toggle"
                    />
                  </div>
                  <p className="text-sm text-[hsl(var(--muted-foreground))]">
                    {networkMode.is_mainnet 
                      ? '🔴 ACTIVO - Transacciones con dinero real'
                      : '🟡 INACTIVO - Usando red de pruebas'
                    }
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-[hsl(var(--muted-foreground))]">Red Actual</Label>
                  <p className="font-mono text-sm mt-1 font-semibold">
                    {networkMode.network.toUpperCase()}
                  </p>
                </div>
                <div>
                  <Label className="text-xs text-[hsl(var(--muted-foreground))]">RPC URL</Label>
                  <p className="font-mono text-sm mt-1 truncate" title={networkMode.rpc_url}>
                    {networkMode.rpc_url}
                  </p>
                </div>
              </div>

              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Nota:</strong> Al cambiar el modo de red, el backend debe reiniciarse para que los cambios surtan efecto.
                </p>
              </div>
            </div>
          )}
        </Card>

        {/* Admin Profile Section */}
        <Card className="p-6">
          <h3 className="font-semibold text-[hsl(var(--brand-ink))] mb-4">Perfil de Administrador</h3>
          <div className="space-y-4">
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={admin?.email || ''}
                readOnly
                className="mt-2"
                data-testid="admin-email"
              />
            </div>
            <div>
              <Label>Nombre Completo</Label>
              <Input
                type="text"
                value={admin?.full_name || ''}
                readOnly
                className="mt-2"
                data-testid="admin-name"
              />
            </div>
            <div>
              <Label>Rol</Label>
              <Input
                type="text"
                value={admin?.role || ''}
                readOnly
                className="mt-2"
                data-testid="admin-role"
              />
            </div>
          </div>
        </Card>
      </div>

      {/* Mainnet Warning Dialog */}
      <AlertDialog open={showMainnetWarning} onOpenChange={setShowMainnetWarning}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-6 h-6" />
              ⚠️ ADVERTENCIA CRÍTICA
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3 text-base">
              <p className="font-semibold text-gray-900">
                Estás a punto de activar el MODO PRODUCCIÓN (MAINNET)
              </p>
              
              <div className="bg-red-50 border border-red-200 p-3 rounded">
                <p className="text-red-800 font-medium">
                  🔴 DINERO REAL - TRANSACCIONES IRREVERSIBLES
                </p>
              </div>

              <ul className="space-y-2 text-sm text-gray-700">
                <li>✓ Todas las transacciones usarán USDT real en Tron Mainnet</li>
                <li>✓ Las transacciones NO se pueden revertir</li>
                <li>✓ Debes tener TRX real para fees</li>
                <li>✓ La wallet concentradora debe estar protegida</li>
              </ul>

              <p className="text-sm font-semibold text-gray-900">
                ¿Estás completamente seguro de activar MAINNET?
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={changing}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => changeNetworkMode('mainnet')}
              disabled={changing}
              className="bg-red-600 hover:bg-red-700"
            >
              {changing ? 'Activando...' : 'SÍ, Activar MAINNET'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}
